/**
 * EditAssessmentPilot Component
 * ORCA360 - Pilot Competency Assessment Editor
 *
 * Allows instructors to review and edit AI-generated pilot assessments
 * Features: Score editing, evidence validation, analysis editing, goal setting
 *
 * Sprint 4 Enhancements:
 * - Auto-save with debouncing (3-second delay)
 * - Audit logging for compliance
 * - PDF export with ORCA360 branding
 * - Unsaved changes warning
 * - Real-time save status indicator
 * - Split-screen layout with transcript viewer (Day 4)
 * - Click evidence timestamp to jump to transcript location
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { MoreVertical } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useDevice } from '../contexts/DeviceContext';
import { useAuditLog } from '../hooks/useAuditLog';
import { useAutoSave } from '../hooks/useAutoSave';
import { createVersion } from '../lib/assessment-versions';
import { usePDFExportPilotAssessment } from '../hooks/usePDFExportPilotAssessment';
import { useWordExportPilotAssessment } from '../hooks/useWordExportPilotAssessment';
import { useRCAPolling } from '../hooks/useRCAPolling';
import { format } from 'date-fns';
import { EvidenceEditModal } from '../components/EvidenceEditModal';
import { LLMValidationPanel, type Evidence as LLMEvidence } from '../components/LLMValidationPanel';
import { AddEvidenceModal } from '../components/AddEvidenceModal';
import { VersionHistoryDrawer } from '../components/VersionHistoryDrawer';
import TranscriptViewer from '../components/TranscriptViewer';
// V2 Assessment Part Components
import {
  ORCA360PartA,
  ORCA360PartB,
  ORCA360PartC,
  ORCA360PartD,
  type PartAData,
  type PartBData,
  type PartCData,
  type PartDData,
} from '../components/assessment';
import {
  mapAssessmentsToPartC,
  mapAssessmentsToPartD,
  extractTEMFromAssessments,
  mapORCA360PartBToExport,
  type PilotAssessmentExportData,
  type PilotAssessmentPartA,
  type AssessmentPilotRow,
  type ORCA360PartBDatabase,
} from '../types/pilot-assessment-export';

interface Evidence {
  quote: string;
  timestamp: string;
  validation_status: string;
  confidence?: number;
  // Tier 4 LLM Validation (optional - only present if LLM validation completed)
  llm_decision?: 'ACCEPTED' | 'REJECTED' | 'RECLASSIFIED';
  llm_reasoning?: string;
  reclassified_to_competency?: string;
  // Observable Behavior codes (ICAO/CBTA compliant)
  obCode?: string; // e.g., "PRO 2.1", "COM 3.3"
  obCodeSource?: 'llm' | 'pattern_match' | 'fallback';
}

interface Assessment {
  id: string;
  session_id: string;
  trainee_id: string;
  pilot_role?: PilotCrewRole;
  competency: string;
  score: number;
  evidence: Evidence[];
  analysis: string;
  goals: string;
  status: 'draft' | 'under_review' | 'approved' | 'published';
  version: number;
  created_at: string;
  updated_at: string;
}

type PilotCrewRole = 'captain' | 'captain_2' | 'first_officer' | 'first_officer_2';

/** Display label for a crew role */
function crewRoleLabel(role: string): string {
  switch (role) {
    case 'captain': return 'Captain';
    case 'captain_2': return 'Captain 2';
    case 'first_officer': return 'First Officer';
    case 'first_officer_2': return 'First Officer 2';
    default: return role;
  }
}

interface SessionInfo {
  trainee_name: string;
  trainee_licence_id: string;
  trainee_user_id: string | null;
  instructor_name: string;
  instructor_licence_id: string;
  instructor_user_id: string | null;
  pilot_role: PilotCrewRole | null;
  pilot_id: string | null;
  session_date: string;
  session_start_time: string | null;
  session_end_time: string | null;
  captain_pf_minutes: number | null;
  fo_pf_minutes: number | null;
  time_pf_minutes: number | null; // legacy fallback (Migration 074)
  time_pm_minutes: number | null; // legacy fallback (Migration 074)
  aircraft_type: string;
  flight_phase: string;
  session_type: string;
  session_type_obj?: { name: string; code: string } | null;
  scenario_code?: string | null;
  location: string | null;
  simulator_id: string | null;
  overall_result: string | null;
  pilot_function: string | null;
}

interface PilotCrewMember {
  user_id: string;
  crew_role: PilotCrewRole;
  full_name: string;
  licence_id?: string | null;
}

function getPilotTimeMinutes(sessionInfo: SessionInfo): { pfMinutes: number | null; pmMinutes: number | null } {
  const role = sessionInfo.pilot_role || '';
  const isCaptain = role.startsWith('captain');
  const isFirstOfficer = role.startsWith('first_officer');

  if (isCaptain) {
    return {
      pfMinutes: sessionInfo.captain_pf_minutes ?? sessionInfo.time_pf_minutes,
      pmMinutes: sessionInfo.fo_pf_minutes ?? sessionInfo.time_pm_minutes,
    };
  }

  if (isFirstOfficer) {
    return {
      pfMinutes: sessionInfo.fo_pf_minutes ?? sessionInfo.time_pf_minutes,
      pmMinutes: sessionInfo.captain_pf_minutes ?? sessionInfo.time_pm_minutes,
    };
  }

  return {
    pfMinutes: sessionInfo.time_pf_minutes,
    pmMinutes: sessionInfo.time_pm_minutes,
  };
}

const COMPETENCY_NAMES: Record<string, string> = {
  PRO: 'Application of Procedures',
  COM: 'Communication',
  LTW: 'Leadership & Teamwork',
  PSD: 'Problem Solving & Decision Making',
  SAW: 'Situational Awareness',
  WLM: 'Workload Management',
  KNO: 'Application of Knowledge',
  FPA: 'Flight Path Management - Automation',
  FPM: 'Flight Path Management - Manual Control',
};

// Timestamp highlight duration when clicking evidence to jump to transcript
const TIMESTAMP_HIGHLIGHT_DURATION_MS = 5000;

export default function EditAssessmentPilot() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { isMobile } = useDevice();
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pilotCrew, setPilotCrew] = useState<PilotCrewMember[]>([]);
  const [selectedPilotRole, setSelectedPilotRole] = useState<PilotCrewRole | null>(null);
  const [selectedCompetency, setSelectedCompetency] = useState<string | null>(null);
  const [editedAssessment, setEditedAssessment] = useState<Assessment | null>(null);
  const [originalAssessment, setOriginalAssessment] = useState<Assessment | null>(null);

  // Evidence modal states
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [selectedEvidenceIndex, setSelectedEvidenceIndex] = useState<number | null>(null);
  
  // Split-screen transcript viewer state
  const [transcriptIds, setTranscriptIds] = useState<string[]>([]);
  const [activeTimestamp, setActiveTimestamp] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(false); // Toggle transcript panel - hidden by default

  // ORCA360 V2 Part B TEM data from database
  const [v2PartB, setV2PartB] = useState<ORCA360PartBDatabase | null>(null);

  // Whether instructor assessments exist for this session
  const [hasInstructorAssessments, setHasInstructorAssessments] = useState(false);

  // Export dropdown state
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  // Mobile overflow menu state
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Autosave localStorage plumbing is retained, but autosave stays locked OFF.
  const [autosaveEnabled] = useState<boolean>(() => {
    const stored = localStorage.getItem('orca360_autosave_enabled');
    if (stored !== null) return false;
    return false;
  });

  // Edit mode — assessments load read-only; Edit button enables editing
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Version history drawer
  const [showVersionHistory, setShowVersionHistory] = useState(false);

  // View mode: 'editor' for detailed editing, 'report' for V2 summary view
  const [searchParams] = useSearchParams();
  const [viewMode, setViewMode] = useState<'editor' | 'report'>(
    searchParams.get('view') === 'report' ? 'report' : 'editor'
  );

  // Hooks
  const { logAction } = useAuditLog();

  useEffect(() => {
    localStorage.setItem('orca360_autosave_enabled', String(autosaveEnabled));
  }, [autosaveEnabled]);

  // CBTA Trainee Sim Assessment exports
  const { exportPilotAssessmentToPDF, isExporting: isExportingCBTAPDF } = usePDFExportPilotAssessment({
    onExportSuccess: (blob, filename) => {
      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      alert('CBTA Assessment PDF exported successfully!');
    },
    onExportError: (err) => {
      alert(`Failed to export CBTA PDF: ${err.message}`);
    },
  });

  const { exportPilotAssessmentToWord, isExporting: isExportingCBTAWord } = useWordExportPilotAssessment({
    onExportSuccess: (blob, filename) => {
      // Trigger download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      alert('CBTA Assessment Word document exported successfully!');
    },
    onExportError: (err) => {
      alert(`Failed to export CBTA Word: ${err.message}`);
    },
  });

  // RCA and SMART Goals polling
  const rcaStatus = useRCAPolling(sessionId, true);

  const loadAssessments = useCallback(async () => {
    if (!sessionId) {
      setError('No session ID provided');
      setLoading(false);
      return;
    }

    try {
      console.log('[EditAssessmentPilot] Loading session:', sessionId);
      
      // Load session info (all fields from Migration 074 for Part A display)
      const { data: session, error: sessionError } = await supabase
        .from('sessions')
        .select(`
          session_date,
          session_start_time,
          session_end_time,
          captain_pf_minutes,
          fo_pf_minutes,
          time_pf_minutes,
          time_pm_minutes,
          aircraft_type,
          flight_phase,
          session_type,
          session_type_obj:session_type_id(name, code),
          scenario_code,
          location,
          simulator_id,
          overall_result,
          pilot_function,
          trainee:trainee_id(id, full_name, licence_id),
          instructor:instructor_id(id, full_name, licence_id)
        `)
        .eq('id', sessionId)
        .single();

      console.log('[EditAssessmentPilot] Session data:', session, 'Error:', sessionError);

      if (sessionError) {
        setError(`Failed to load session: ${sessionError.message} (Code: ${sessionError.code})`);
        throw sessionError;
      }

      if (!session) {
        setError('Session not found');
        return;
      }

      const traineeData = Array.isArray(session.trainee) ? session.trainee[0] : session.trainee;
      const instructorData = Array.isArray(session.instructor) ? session.instructor[0] : session.instructor;

      // Fetch crew with org_pilot fallback for non-registered pilots
      const { data: crewData, error: crewError } = await supabase
        .from('session_crew')
        .select('user_id, org_pilot_id, crew_role, user:users(full_name, licence_id), org_pilot:org_pilots(full_name)')
        .eq('session_id', sessionId);

      if (crewError) {
        console.warn('[EditAssessmentPilot] Unable to load session crew:', crewError.message);
      }

      const crewMembers: PilotCrewMember[] = crewData && crewData.length > 0
        ? crewData.map((row) => {
            const userData = Array.isArray(row.user) ? row.user[0] : row.user;
            const orgPilotData = Array.isArray(row.org_pilot) ? row.org_pilot[0] : row.org_pilot;
            return {
              user_id: row.user_id || row.org_pilot_id || '',
              crew_role: row.crew_role,
              full_name: userData?.full_name || orgPilotData?.full_name || 'Unknown',
              licence_id: userData?.licence_id || '',
            };
          })
        : [{
            user_id: traineeData?.id || '',
            crew_role: session.pilot_function === 'Captain' ? 'captain' : 'first_officer',
            full_name: traineeData?.full_name || 'Unknown',
            licence_id: traineeData?.licence_id || '',
          }];

      // Check for pilot roles that have assessments but no session_crew entry
      // (e.g., session created before migration 095, or crew not set in SessionForm)
      const { data: assessmentRoles } = await supabase
        .from('assessments_pilot')
        .select('pilot_role')
        .eq('session_id', sessionId)
        .not('pilot_role', 'is', null);

      if (assessmentRoles && assessmentRoles.length > 0) {
        const distinctRoles = [...new Set(assessmentRoles.map(r => r.pilot_role))];
        for (const role of distinctRoles) {
          if (!crewMembers.find(m => m.crew_role === role)) {
            crewMembers.push({
              user_id: traineeData?.id || '',
              crew_role: role as PilotCrewRole,
              full_name: crewRoleLabel(role),
              licence_id: '',
            });
          }
        }
      }

      setPilotCrew(crewMembers);

      const activePilotRole = selectedPilotRole || crewMembers[0]?.crew_role || null;
      if (!selectedPilotRole && activePilotRole) {
        setSelectedPilotRole(activePilotRole);
      }

      const activePilot = crewMembers.find((member) => member.crew_role === activePilotRole) || crewMembers[0];

      setSessionInfo({
        trainee_name: activePilot?.full_name || 'Unknown',
        trainee_licence_id: activePilot?.licence_id || '',
        trainee_user_id: activePilot?.user_id || null,
        instructor_name: instructorData?.full_name || 'Unknown',
        instructor_licence_id: instructorData?.licence_id || '',
        instructor_user_id: instructorData?.id || null,
        pilot_role: activePilot?.crew_role || null,
        pilot_id: activePilot?.user_id || null,
        session_date: session.session_date,
        session_start_time: session.session_start_time,
        session_end_time: session.session_end_time,
        captain_pf_minutes: session.captain_pf_minutes,
        fo_pf_minutes: session.fo_pf_minutes,
        time_pf_minutes: session.time_pf_minutes,
        time_pm_minutes: session.time_pm_minutes,
        aircraft_type: session.aircraft_type,
        flight_phase: session.flight_phase || 'Type Rating',
        session_type: session.session_type || 'Training',
        session_type_obj: (session as any).session_type_obj ?? null,
        scenario_code: (session as any).scenario_code ?? null,
        location: session.location,
        simulator_id: session.simulator_id,
        overall_result: session.overall_result,
        pilot_function: crewRoleLabel(activePilot?.crew_role || 'first_officer'),
      });

      // Load ALL transcripts for split-screen view (briefing, session(s), debriefing)
      console.log('[EditAssessmentPilot] Loading transcripts for session:', sessionId);
      const { data: transcriptData, error: transcriptError } = await supabase
        .from('transcripts')
        .select('id, created_at')
        .eq('session_id', sessionId)
        .or('is_archived.is.null,is_archived.eq.false')
        .order('created_at', { ascending: true }); // Chronological order

      if (transcriptError) {
        console.warn('[EditAssessmentPilot] No transcripts found:', transcriptError.message);
      } else if (transcriptData && transcriptData.length > 0) {
        const ids = transcriptData.map(t => t.id);
        console.log('[EditAssessmentPilot] Loaded', ids.length, 'transcript(s):', ids);
        setTranscriptIds(ids);
      }

      // Load ORCA360 V2 Part B TEM data from orca360_assessments table
      // activePilot.user_id may be a users.id OR an org_pilots.id — check both columns
      console.log('[EditAssessmentPilot] Loading V2 TEM data for session:', sessionId);
      const pilotId = activePilot?.user_id || '';
      // Find the matching crew row to determine if this is an org_pilot
      const activeCrew = crewData?.find((row) => row.user_id === pilotId || row.org_pilot_id === pilotId);
      const isOrgPilot = activeCrew ? !activeCrew.user_id && !!activeCrew.org_pilot_id : false;

      let orca360Query = supabase
        .from('orca360_assessments')
        .select('part_b')
        .eq('session_id', sessionId);

      if (isOrgPilot) {
        orca360Query = orca360Query.eq('org_pilot_id', pilotId);
      } else {
        orca360Query = orca360Query.eq('trainee_id', pilotId);
      }

      const { data: orca360Data, error: orca360Error } = await orca360Query
        .maybeSingle(); // Use maybeSingle to handle missing records gracefully

      if (orca360Error) {
        console.warn('[EditAssessmentPilot] Could not load V2 TEM data:', orca360Error.message);
      } else if (orca360Data?.part_b) {
        console.log('[EditAssessmentPilot] Loaded V2 Part B TEM data:', orca360Data.part_b);
        setV2PartB(orca360Data.part_b as ORCA360PartBDatabase);
      } else {
        console.log('[EditAssessmentPilot] No V2 TEM data available, will use fallback extraction');
        setV2PartB(null);
      }

      // Check if instructor assessments exist for this session
      const { count: instructorCount } = await supabase
        .from('assessments_instructor')
        .select('id', { count: 'exact', head: true })
        .eq('session_id', sessionId);
      setHasInstructorAssessments((instructorCount ?? 0) > 0);

      // Load assessments
      console.log('[EditAssessmentPilot] Loading assessments for session:', sessionId);
      const pilotRoleFilter = activePilotRole || 'first_officer';
      const { data: assessmentData, error: assessmentError } = await supabase
        .from('assessments_pilot')
        .select('*')
        .eq('session_id', sessionId)
        .or(`pilot_role.eq.${pilotRoleFilter},pilot_role.is.null`)
        .order('competency', { ascending: true });

      console.log('[EditAssessmentPilot] Assessment data:', assessmentData, 'Error:', assessmentError);

      if (assessmentError) {
        setError(`Failed to load assessments: ${assessmentError.message} (Code: ${assessmentError.code})`);
        console.error('[EditAssessmentPilot] RLS Policy might be blocking access. Check if user has instructor role.');
        throw assessmentError;
      }

      if (!assessmentData || assessmentData.length === 0) {
        setError(`No pilot assessments found for ${crewRoleLabel(activePilotRole || 'first_officer')}. Please generate assessments first.`);
        setAssessments([]);
        return;
      }

      setAssessments(assessmentData);
      setSelectedCompetency(assessmentData[0].competency);
      setEditedAssessment(assessmentData[0]);
      setOriginalAssessment(assessmentData[0]);

      console.log('[EditAssessmentPilot] Successfully loaded', assessmentData.length, 'assessments');
    } catch (err: any) {
      console.error('[EditAssessmentPilot] Error loading assessments:', err);
      if (!error) {
        setError(`Failed to load assessments: ${err.message || 'Unknown error'}`);
      }
    } finally {
      setLoading(false);
    }
  }, [sessionId, error, selectedPilotRole]); // Memoized with sessionId dependency

  useEffect(() => {
    loadAssessments();
  }, [loadAssessments]);

  // Auto-refresh when RCA/SMART goals generation completes
  useEffect(() => {
    if (rcaStatus.isComplete && !rcaStatus.isGenerating) {
      console.log('[EditAssessmentPilot] RCA/SMART goals generation completed, refreshing assessments...');
      loadAssessments();
    }
  }, [rcaStatus.isComplete, rcaStatus.isGenerating, loadAssessments]);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Auto-save callback (debounced through useAutoSave hook)
  const handleAutoSave = useCallback(async (data: Assessment) => {
    if (!data || !sessionId) return;

    const changes: Record<string, { old: unknown; new: unknown }> = {};
    if (originalAssessment) {
      if (data.score !== originalAssessment.score) {
        changes.score = { old: originalAssessment.score, new: data.score };
      }
      if (data.analysis !== originalAssessment.analysis) {
        changes.analysis = { old: originalAssessment.analysis, new: data.analysis };
      }
      if (data.goals !== originalAssessment.goals) {
        changes.goals = { old: originalAssessment.goals, new: data.goals };
      }
    }

    const { error: updateError } = await supabase
      .from('assessments_pilot')
      .update({
        score: data.score,
        analysis: data.analysis,
        goals: data.goals,
        evidence: data.evidence,
      })
      .eq('id', data.id);

    if (updateError) {
      throw updateError;
    }

    if (Object.keys(changes).length > 0) {
      await logAction({
        action: 'assessment_updated',
        entity_type: 'assessment_pilot',
        entity_id: data.id,
        changes,
        metadata: {
          session_id: sessionId,
          competency: data.competency,
        },
      });
    }

    setOriginalAssessment(data);
    setAssessments((prevAssessments) =>
      prevAssessments.map((assessment) =>
        assessment.id === data.id ? data : assessment,
      ),
    );
  }, [originalAssessment, sessionId, logAction]);

  // Explicit save handler
  const handleSave = useCallback(async () => {
    if (!editedAssessment || !sessionId) return;
    setIsSaving(true);
    try {
      const changes: Record<string, { old: unknown; new: unknown }> = {};
      if (originalAssessment) {
        if (editedAssessment.score !== originalAssessment.score)
          changes.score = { old: originalAssessment.score, new: editedAssessment.score };
        if (editedAssessment.analysis !== originalAssessment.analysis)
          changes.analysis = { old: originalAssessment.analysis, new: editedAssessment.analysis };
        if (editedAssessment.goals !== originalAssessment.goals)
          changes.goals = { old: originalAssessment.goals, new: editedAssessment.goals };
      }

      const nextVersion = (editedAssessment.version ?? 1) + 1;

      const { error: updateError } = await supabase
        .from('assessments_pilot')
        .update({
          score: editedAssessment.score,
          analysis: editedAssessment.analysis,
          goals: editedAssessment.goals,
          evidence: editedAssessment.evidence,
          version: nextVersion,
        })
        .eq('id', editedAssessment.id);

      if (updateError) throw updateError;

      if (Object.keys(changes).length > 0) {
        await logAction({
          action: 'assessment_updated',
          entity_type: 'assessment_pilot',
          entity_id: editedAssessment.id,
          changes,
          metadata: { session_id: sessionId, competency: editedAssessment.competency },
        });
      }

      // Version snapshot — only for assessments with an AI baseline (skip legacy)
      const { data: existingVersions } = await supabase
        .from('assessment_versions')
        .select('id')
        .eq('assessment_id', editedAssessment.id)
        .eq('assessment_type', 'pilot')
        .limit(1);

      if (existingVersions && existingVersions.length > 0) {
        const { data: { user } } = await supabase.auth.getUser();
        await createVersion(
          editedAssessment.id,
          'pilot',
          { ...editedAssessment, version: nextVersion } as Record<string, unknown>,
          user?.id ?? null,
          { isAiBaseline: false }
        );
      }

      const saved = { ...editedAssessment, version: nextVersion };
      setOriginalAssessment(saved);
      setEditedAssessment(saved);
      setAssessments((prev) => prev.map((a) => (a.id === saved.id ? saved : a)));
      setIsEditMode(false);
    } catch (err) {
      console.error('[EditAssessmentPilot] Save error:', err);
      alert('Failed to save assessment');
    } finally {
      setIsSaving(false);
    }
  }, [editedAssessment, originalAssessment, sessionId, logAction]);

  const handleVersionRestore = useCallback(
    async (snapshot: Record<string, unknown>, fromVersionNumber: number) => {
      if (!editedAssessment) return;
      const nextVersion = (editedAssessment.version ?? 1) + 1;

      const { error } = await supabase
        .from('assessments_pilot')
        .update({
          score: snapshot.score as number,
          analysis: snapshot.analysis as string,
          goals: snapshot.goals as string,
          evidence: snapshot.evidence,
          version: nextVersion,
        })
        .eq('id', editedAssessment.id);
      if (error) throw error;

      const { data: { user } } = await supabase.auth.getUser();
      await createVersion(
        editedAssessment.id,
        'pilot',
        { ...snapshot, version: nextVersion },
        user?.id ?? null,
        { isAiBaseline: false, changeSummary: `Restored from version ${fromVersionNumber}` },
      );

      await logAction({
        action: 'assessment_updated',
        entity_type: 'assessment_pilot',
        entity_id: editedAssessment.id,
        metadata: { session_id: sessionId, restored_from_version: fromVersionNumber },
      });

      const restored: Assessment = {
        ...editedAssessment,
        score: snapshot.score as number,
        analysis: snapshot.analysis as string,
        goals: snapshot.goals as string,
        evidence: (snapshot.evidence ?? []) as Evidence[],
        version: nextVersion,
      };
      setEditedAssessment(restored);
      setOriginalAssessment(restored);
      setAssessments((prev) => prev.map((a) => (a.id === restored.id ? restored : a)));
    },
    [editedAssessment, sessionId, logAction],
  );

  const handleEdit = useCallback(() => setIsEditMode(true), []);

  const handleCancel = useCallback(() => {
    if (originalAssessment) setEditedAssessment(originalAssessment);
    setIsEditMode(false);
  }, [originalAssessment]);

  // Auto-save hook
  const autoSave = useAutoSave({
    data: editedAssessment!,
    onSave: handleAutoSave,
    enabled: editedAssessment !== null && autosaveEnabled && isEditMode,
    delay: 3000,
  });

  // Unsaved changes guard on browser close/refresh
  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (isEditMode) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isEditMode]);

  const handleCompetencySelect = (competency: string) => {
    if (isEditMode) {
      if (!confirm('You have unsaved changes. Discard?')) return;
      setIsEditMode(false);
    }
    const assessment = assessments.find((a) => a.competency === competency);
    if (assessment) {
      setSelectedCompetency(competency);
      setEditedAssessment(assessment);
      setOriginalAssessment(assessment);
    }
  };

  const handleScoreChange = (score: number) => {
    if (editedAssessment) {
      setEditedAssessment({ ...editedAssessment, score });
    }
  };

  const handleAnalysisChange = (analysis: string) => {
    if (editedAssessment) {
      setEditedAssessment({ ...editedAssessment, analysis });
    }
  };

  const handleGoalsChange = (goals: string) => {
    if (editedAssessment) {
      setEditedAssessment({ ...editedAssessment, goals });
    }
  };

  // Click-to-jump: Click evidence timestamp to highlight in transcript
  const handleTimestampClick = (timestamp: string) => {
    console.log('[EditAssessmentPilot] Jumping to timestamp:', timestamp);
    setActiveTimestamp(timestamp);
    // Enable transcript panel if it's hidden
    if (!showTranscript) {
      setShowTranscript(true);
    }
    // Clear active timestamp after highlight duration (fade highlight)
    setTimeout(() => {
      setActiveTimestamp(null);
    }, TIMESTAMP_HIGHLIGHT_DURATION_MS);
  };

  // Evidence management handlers
  const handleEditEvidence = (index: number) => {
    setSelectedEvidenceIndex(index);
    setIsEditModalOpen(true);
  };

  const handleBulkLLMValidation = useCallback((updatedEvidence: LLMEvidence[]) => {
    if (!editedAssessment) return;
    setEditedAssessment({ ...editedAssessment, evidence: updatedEvidence as Evidence[] });
  }, [editedAssessment]);

  const handleSaveEvidence = async (updatedEvidence: Evidence) => {
    if (!editedAssessment || selectedEvidenceIndex === null) return;

    const newEvidence = [...editedAssessment.evidence];
    const oldEvidence = newEvidence[selectedEvidenceIndex];
    newEvidence[selectedEvidenceIndex] = updatedEvidence;

    setEditedAssessment({ ...editedAssessment, evidence: newEvidence });

    // Log the change
    await logAction({
      action: 'evidence_updated',
      entity_type: 'assessment_pilot',
      entity_id: editedAssessment.id,
      changes: {
        evidence: {
          old: oldEvidence,
          new: updatedEvidence,
        },
      },
      metadata: {
        session_id: sessionId,
        competency: editedAssessment.competency,
        evidence_index: selectedEvidenceIndex,
      },
    });
  };

  const handleDeleteEvidence = async (index: number) => {
    if (!editedAssessment) return;

    const newEvidence = [...editedAssessment.evidence];
    const deletedEvidence = newEvidence[index];
    newEvidence.splice(index, 1);

    setEditedAssessment({ ...editedAssessment, evidence: newEvidence });

    // Log the deletion
    await logAction({
      action: 'evidence_deleted',
      entity_type: 'assessment_pilot',
      entity_id: editedAssessment.id,
      changes: {
        evidence: {
          old: deletedEvidence,
          new: null,
        },
      },
      metadata: {
        session_id: sessionId,
        competency: editedAssessment.competency,
        evidence_index: index,
      },
    });
  };

  const handleAddEvidence = async (newEvidence: Evidence) => {
    if (!editedAssessment) return;

    const updatedEvidence = [...editedAssessment.evidence, newEvidence];
    setEditedAssessment({ ...editedAssessment, evidence: updatedEvidence });

    // Log the addition
    await logAction({
      action: 'evidence_added',
      entity_type: 'assessment_pilot',
      entity_id: editedAssessment.id,
      changes: {
        evidence: {
          old: null,
          new: newEvidence,
        },
      },
      metadata: {
        session_id: sessionId,
        competency: editedAssessment.competency,
      },
    });
  };

  // Keyboard shortcut for save (Ctrl+S / Cmd+S)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        if (isEditMode) handleSave();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isEditMode, handleSave]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.export-dropdown-container')) {
        setShowExportDropdown(false);
      }
    };

    if (showExportDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showExportDropdown]);

  // Build export data for CBTA Trainee Sim Assessment
  const buildCBTAExportData = useCallback((): PilotAssessmentExportData | null => {
    if (!sessionInfo || assessments.length === 0) return null;

    const { pfMinutes, pmMinutes } = getPilotTimeMinutes(sessionInfo);

    // Map session info to Part A (using real data from Migration 074 fields)
    // Use licence_id first, then user ID as fallback, then 'N/A'
    const traineeId = sessionInfo.trainee_licence_id || sessionInfo.trainee_user_id || 'N/A';
    const instructorId = sessionInfo.instructor_licence_id || sessionInfo.instructor_user_id || 'N/A';

    const partA: PilotAssessmentPartA = {
      trainee: {
        name: sessionInfo.trainee_name,
        id: traineeId,
      },
      instructor: {
        name: sessionInfo.instructor_name,
        id: instructorId,
      },
      aircraftType: sessionInfo.aircraft_type,
      sessionDate: new Date(sessionInfo.session_date).toLocaleDateString('en-GB').replace(/\//g, '-'),
      startTime: sessionInfo.session_start_time
        ? new Date(sessionInfo.session_start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : 'Not recorded',
      endTime: sessionInfo.session_end_time
        ? new Date(sessionInfo.session_end_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : 'Not recorded',
      timeAsPF: {
        hours: Math.floor((pfMinutes || 0) / 60),
        minutes: (pfMinutes || 0) % 60,
      },
      timeAsPM: {
        hours: Math.floor((pmMinutes || 0) / 60),
        minutes: (pmMinutes || 0) % 60,
      },
      location: sessionInfo.location || 'Not specified',
      simulatorId: sessionInfo.simulator_id || 'N/A',
      sessionName: [
        sessionInfo.session_type_obj?.name || sessionInfo.session_type,
        sessionInfo.scenario_code,
        sessionInfo.flight_phase,
      ].filter(Boolean).join(' - ') || 'Simulator Training',
      sessionType: sessionInfo.session_type_obj?.name || sessionInfo.session_type,
      flightPhase: sessionInfo.flight_phase,
      overallResult: (sessionInfo.overall_result as 'Pass' | 'Fail' | 'Incomplete') || 'Incomplete',
      pilotFunction: sessionInfo.pilot_role
        ? crewRoleLabel(sessionInfo.pilot_role) as 'Captain' | 'First Officer' | 'Instructor Trainee' | 'Other'
        : (sessionInfo.pilot_function as 'Captain' | 'First Officer' | 'Instructor Trainee' | 'Other') || 'Other',
    };

    // Map assessments to the export format
    const assessmentRows: AssessmentPilotRow[] = assessments.map(a => ({
      id: a.id,
      session_id: a.session_id,
      trainee_id: a.trainee_id,
      pilot_role: a.pilot_role,
      competency: a.competency,
      score: a.score,
      evidence: a.evidence.map(e => ({
        quote: e.quote,
        timestamp: e.timestamp,
        validation_status: e.validation_status as any,
        confidence: e.confidence,
        llm_decision: e.llm_decision,
        llm_reasoning: e.llm_reasoning,
        obCode: e.obCode,
        obCodeSource: e.obCodeSource,
      })),
      analysis: a.analysis,
      goals: a.goals,
      status: a.status === 'approved' || a.status === 'published' ? 'published' : 'draft',
      version: a.version,
      ai_generated: true,
      created_at: a.created_at,
      updated_at: a.updated_at,
    }));

    // Use real AI-generated TEM data if available, fallback to heuristic extraction
    const partB = v2PartB
      ? mapORCA360PartBToExport(v2PartB)
      : extractTEMFromAssessments(assessmentRows);

    console.log('[EditAssessmentPilot] Building export with Part B:', {
      usingV2Data: !!v2PartB,
      threatsCount: partB.threats.length,
      errorsCount: partB.errors.length,
    });

    return {
      partA,
      partB,
      partC: mapAssessmentsToPartC(assessmentRows),
      partD: mapAssessmentsToPartD(assessmentRows),
      invocationLine: 'Please verify the assessment and inform the company.',
      metadata: {
        generatedAt: new Date().toISOString(),
        version: '1.0',
        traineeId: assessments[0]?.trainee_id || '',
        pilotRole: sessionInfo.pilot_role || undefined,
      },
    };
  }, [sessionInfo, assessments, v2PartB]);

  // Build V2 Part A data for online view
  const v2PartAData = useMemo((): PartAData | null => {
    if (!sessionInfo) return null;

    const { pfMinutes, pmMinutes } = getPilotTimeMinutes(sessionInfo);

    const formatTime = (minutes: number | null): string => {
      if (!minutes) return '00:00';
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };

    // Use licence_id first, then user ID as fallback, then 'N/A'
    const traineeId = sessionInfo.trainee_licence_id || sessionInfo.trainee_user_id || 'N/A';
    const instructorId = sessionInfo.instructor_licence_id || sessionInfo.instructor_user_id || 'N/A';

    return {
      traineeName: sessionInfo.trainee_name,
      traineeId,
      instructorName: sessionInfo.instructor_name,
      instructorId,
      aircraftType: sessionInfo.aircraft_type,
      sessionDate: new Date(sessionInfo.session_date).toLocaleDateString('en-GB').replace(/\//g, '-'),
      startTime: sessionInfo.session_start_time
        ? new Date(sessionInfo.session_start_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : 'Not recorded',
      endTime: sessionInfo.session_end_time
        ? new Date(sessionInfo.session_end_time).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
        : 'Not recorded',
      timeAsPF: formatTime(pfMinutes),
      timeAsPM: formatTime(pmMinutes),
      location: sessionInfo.location || 'Not specified',
      simulatorId: sessionInfo.simulator_id || 'N/A',
      sessionName: [
        sessionInfo.session_type_obj?.name || sessionInfo.session_type,
        sessionInfo.scenario_code,
        sessionInfo.flight_phase,
      ].filter(Boolean).join(' - ') || 'Simulator Training',
      overallResult: sessionInfo.overall_result || 'Incomplete',
      function: sessionInfo.pilot_role
        ? crewRoleLabel(sessionInfo.pilot_role)
        : sessionInfo.pilot_function || 'Not specified',
    };
  }, [sessionInfo]);

  // Build V2 Part B (TEM) data for online view
  const v2PartBData = useMemo((): PartBData | null => {
    if (!assessments.length) return null;

    // Use real AI-generated TEM data if available, fallback to heuristic extraction
    const assessmentRows: AssessmentPilotRow[] = assessments.map(a => ({
      id: a.id,
      session_id: a.session_id,
      trainee_id: a.trainee_id,
      competency: a.competency,
      score: a.score,
      evidence: a.evidence.map(e => ({
        quote: e.quote,
        timestamp: e.timestamp,
        validation_status: e.validation_status as any,
        confidence: e.confidence,
        llm_decision: e.llm_decision,
        llm_reasoning: e.llm_reasoning,
        obCode: e.obCode,
        obCodeSource: e.obCodeSource,
      })),
      analysis: a.analysis,
      goals: a.goals,
      status: a.status === 'approved' || a.status === 'published' ? 'published' : 'draft',
      version: a.version,
      ai_generated: true,
      created_at: a.created_at,
      updated_at: a.updated_at,
    }));

    const partBExport = v2PartB
      ? mapORCA360PartBToExport(v2PartB)
      : extractTEMFromAssessments(assessmentRows);

    // Transform threats with proper type handling and defaults
    const transformedThreats = partBExport.threats.map(t => ({
      category: t.category,
      description: t.description || 'No description provided',
      primaryCompetency: String(t.primaryCompetency),
      secondaryCompetencies: (t.secondaryCompetencies || []).map(String),
      management: t.management,
      evidence: t.evidence,
    }));

    // Transform errors - handle both string and object formats
    const transformedErrors = partBExport.errors
      .filter((e): e is NonNullable<typeof e> => e !== null && e !== undefined)
      .map(e => {
        if (typeof e === 'string') {
          return {
            type: 'Other',
            classification: 'Commission',
            description: e,
            linkedThreats: [] as string[],
            ledToUAS: false,
            evidence: undefined,
          };
        }
        return {
          type: e.type || 'Other',
          classification: e.classification || 'Commission',
          description: e.description,
          linkedThreats: [] as string[],
          ledToUAS: e.ledToUAS ?? false,
          evidence: e.evidence,
        };
      });

    // Transform resilience factors - handle both string and object formats
    const transformedResilienceFactors = (partBExport.resilienceFactors || [])
      .filter((r): r is NonNullable<typeof r> => r !== null && r !== undefined)
      .map(r => {
        if (typeof r === 'string') {
          return {
            description: r,
            competencies: [] as string[],
            evidence: undefined,
          };
        }
        return {
          description: r.description,
          competencies: (r.competencies || []).map(String),
          evidence: r.evidence,
        };
      });

    // Get UAS events from v2PartB if available (not in partBExport)
    const uasEvents = v2PartB?.uasEvents?.map(u => ({
      description: u.description,
      severity: u.severity || 'Unspecified',
      recovery: u.recovery,
      evidence: undefined, // UAS events from DB don't include evidence field
    })) || [];

    // Get management summary from v2PartB if available
    const managementSummary = v2PartB?.managementSummary || partBExport.temOverview || 'TEM management analysis based on observed behaviors.';

    return {
      operationalContext: `Training session analyzing pilot competencies in ${sessionInfo?.session_type || 'simulator'} environment.`,
      threats: transformedThreats,
      errors: transformedErrors,
      resilienceFactors: transformedResilienceFactors,
      uasEvents,
      managementSummary,
    };
  }, [assessments, v2PartB, sessionInfo]);

  // Build V2 Part C (Competency Assessment) data for online view
  const v2PartCData = useMemo((): PartCData | null => {
    if (!assessments.length) return null;

    return {
      assessments: assessments.map(a => ({
        competency: a.competency,
        competencyName: COMPETENCY_NAMES[a.competency] || a.competency,
        grade: a.score,
        observableBehaviors: (() => {
          // FIXED: Use evidence obCode field directly (from LLM/pattern matching/fallback)
          // Previously this extracted from analysis text which was unreliable
          return a.evidence.map((e, idx) => ({
            ob: {
              // Use the obCode from evidence, with fallback to generic code
              code: e.obCode || `${a.competency} ${idx + 1}.0`,
              description: '',
            },
            comment: e.quote,
            quote: e.quote,
            timestamp: e.timestamp,
            validationStatus: e.validation_status,
            obCodeSource: e.obCodeSource, // Track source for debugging
          }));
        })(),
        instructorComments: a.analysis,
      })),
      gradeLegend: {
        1: 'Unsatisfactory',
        2: 'Below Standard',
        3: 'Standard',
        4: 'Above Standard',
        5: 'Excellent',
      },
    };
  }, [assessments]);

  // Build V2 Part D (RCA & Recommendations) data for online view
  const v2PartDData = useMemo((): PartDData | null => {
    if (!assessments.length) return null;

    // Get assessments with scores <= 3 (need improvement)
    const focusAssessments = assessments
      .filter(a => a.score <= 3)
      .sort((a, b) => a.score - b.score)
      .slice(0, 4); // Top 4 focus areas

    return {
      focusCompetencies: focusAssessments.map((a, idx) => ({
        focusNumber: idx + 1,
        competency: a.competency,
        competencyName: COMPETENCY_NAMES[a.competency] || a.competency,
        causalFactor: a.analysis || 'Root cause analysis pending.',
        recommendations: a.goals || 'SMART goals pending generation.',
        evidence: a.evidence.map(e => `[${e.timestamp}] "${e.quote}"`),
      })),
      overallStrategy: focusAssessments.length > 0
        ? `Focus on ${focusAssessments.map(a => COMPETENCY_NAMES[a.competency]).join(', ')} in upcoming training sessions.`
        : 'All competencies at or above standard. Continue current training approach.',
    };
  }, [assessments]);

  // CBTA PDF export handler
  const handleExportCBTAPDF = async () => {
    setShowExportDropdown(false); // Close dropdown immediately
    const exportData = buildCBTAExportData();
    if (!exportData) {
      alert('No assessment data available to export');
      return;
    }

    try {
      await exportPilotAssessmentToPDF(exportData);

      // Log export action
      await logAction({
        action: 'assessment_exported',
        entity_type: 'assessment_pilot',
        entity_id: sessionId || '',
        metadata: {
          session_id: sessionId,
          format: 'pdf',
          trainee_name: sessionInfo?.trainee_name,
          export_type: 'cbta_trainee_sim_assessment',
        },
      });
    } catch (err) {
      console.error('Error exporting CBTA PDF:', err);
    }
  };

  // CBTA Word export handler
  const handleExportCBTAWord = async () => {
    setShowExportDropdown(false); // Close dropdown immediately
    const exportData = buildCBTAExportData();
    if (!exportData) {
      alert('No assessment data available to export');
      return;
    }

    try {
      await exportPilotAssessmentToWord(exportData);

      // Log export action
      await logAction({
        action: 'assessment_exported',
        entity_type: 'assessment_pilot',
        entity_id: sessionId || '',
        metadata: {
          session_id: sessionId,
          format: 'docx',
          trainee_name: sessionInfo?.trainee_name,
          export_type: 'cbta_trainee_sim_assessment',
        },
      });
    } catch (err) {
      console.error('Error exporting CBTA Word:', err);
    }
  };

  const handleInstructorAssessment = async () => {
    navigate(`/sessions/${sessionId}/edit-instructor-assessment`);
  };

  const handlePublish = async () => {
    if (isEditMode) {
      alert('Save or cancel your current edits before publishing.');
      return;
    }
    if (!confirm('Publish all assessments for this session? The trainee will be able to view them.')) {
      return;
    }

    try {
      const { error: publishError } = await supabase
        .from('assessments_pilot')
        .update({ status: 'published' })
        .eq('session_id', sessionId);

      if (publishError) throw publishError;

      // Log publish action
      await logAction({
        action: 'assessment_published',
        entity_type: 'assessment_pilot',
        entity_id: sessionId || '',
        metadata: {
          session_id: sessionId,
          trainee_name: sessionInfo?.trainee_name,
        },
      });

      alert('Assessments published successfully!');
      navigate(`/sessions/${sessionId}`);
    } catch (err) {
      console.error('Error publishing assessments:', err);
      alert('Failed to publish assessments');
    }
  };

  const getValidationColor = (status: string) => {
    switch (status) {
      case 'VERIFIED':
        return 'text-green-600 bg-green-50';
      case 'VERBATIM':
        return 'text-green-600 bg-green-50'; // Green like VERIFIED
      case 'PARAPHRASED':
        return 'text-yellow-600 bg-yellow-50';
      case 'SIMILAR':
        return 'text-orange-600 bg-orange-50';
      case 'UNVERIFIED':
        return 'text-red-600 bg-red-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getValidationIcon = (status: string) => {
    switch (status) {
      case 'VERIFIED':
        return '✓';
      case 'VERBATIM':
        return '✓'; // Checkmark like VERIFIED
      case 'PARAPHRASED':
        return '⚠';
      case 'SIMILAR':
        return '⚠';
      case 'UNVERIFIED':
        return '❌';
      default:
        return '?';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orca-elevate"></div>
          <p className="mt-2 text-steel">Loading assessments...</p>
        </div>
      </div>
    );
  }

  if (error || !sessionInfo || assessments.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-semibold text-navy mb-2">No Assessments Found</h2>
          <p className="text-steel mb-6">
            {error || 'No assessments have been generated for this session yet.'}
          </p>
          <button onClick={() => navigate(`/sessions/${sessionId}`)} className="btn-primary">
            Back to Session
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-white">
      {/* Compact Navigation Bar */}
      <div className={`bg-navy flex items-center justify-between shrink-0 shadow-sm ${isMobile ? 'px-2 py-1.5' : 'px-4 py-2'}`}>
        <button
          onClick={() => navigate(`/sessions/${sessionId}`)}
          className={`text-white hover:text-sky-200 font-medium flex items-center gap-1 ${isMobile ? 'text-xs' : 'text-sm gap-2'}`}
        >
          ← Back
        </button>
        {!isMobile && (
          <span className="text-white/70 text-sm">
            {sessionInfo?.trainee_name} • {sessionInfo?.aircraft_type} • {sessionInfo?.session_date ? new Date(sessionInfo.session_date).toLocaleDateString() : ''}
          </span>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-auto">
        <div className={isMobile ? 'py-2' : 'py-6 px-6'}>
          {/* Page Header */}
          <div className={isMobile ? 'mb-2 px-2' : 'mb-6'}>
            <div className={isMobile ? '' : 'flex items-start justify-between'}>
          <div>
            {/* Mobile: title + pilot selector on one line */}
            {isMobile ? (
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg font-bold text-navy">Pilot Assessment</h1>
                {pilotCrew.length > 1 && (
                  <div className="flex rounded-md border border-steel/30 overflow-hidden">
                    {pilotCrew.map((pilot) => (
                      <button
                        key={pilot.crew_role}
                        onClick={() => setSelectedPilotRole(pilot.crew_role)}
                        className={`px-2 py-0.5 text-[10px] font-semibold transition-colors ${
                          sessionInfo.pilot_role === pilot.crew_role
                            ? 'bg-orca-elevate text-white'
                            : 'bg-white text-navy hover:bg-ice'
                        }`}
                      >
                        {pilot.crew_role.startsWith('captain') ? 'CPT' : 'F/O'}{pilot.crew_role.endsWith('_2') ? ' 2' : ''}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <>
                <h1 className="text-3xl font-bold text-navy mb-2">Edit Pilot Assessment</h1>
                <p className="text-steel">
                  {sessionInfo.trainee_name} • {new Date(sessionInfo.session_date).toLocaleDateString()} • {sessionInfo.aircraft_type}
                </p>
                {pilotCrew.length > 1 && (
                  <div className="inline-flex items-center space-x-2 text-xs font-medium text-steel mt-3">
                    <span className="uppercase tracking-wide">Pilot:</span>
                    <div className="flex rounded-md border border-steel/30 overflow-hidden">
                      {pilotCrew.map((pilot) => (
                        <button
                          key={pilot.crew_role}
                          onClick={() => setSelectedPilotRole(pilot.crew_role)}
                          className={`px-2 py-1 text-xs font-semibold transition-colors ${
                            sessionInfo.pilot_role === pilot.crew_role
                              ? 'bg-orca-elevate text-white'
                              : 'bg-white text-navy hover:bg-ice'
                          }`}
                        >
                          {crewRoleLabel(pilot.crew_role)}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {/* Autosave toggle and status */}
                <div className={`flex items-center gap-2 ${pilotCrew.length > 1 ? 'mt-2' : 'mt-3'}`}>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={autosaveEnabled}
                    disabled
                    className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                      autosaveEnabled ? 'bg-green-600' : 'bg-gray-400'
                    }`}
                    title="Auto-save is disabled"
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                        autosaveEnabled ? 'translate-x-4' : 'translate-x-1'
                      }`}
                    />
                  </button>
                  <span className="text-xs text-steel">
                    {autoSave.isSaving
                      ? 'Saving...'
                      : autoSave.hasUnsavedChanges
                      ? 'Unsaved'
                      : autoSave.lastSaved
                      ? `Saved ${format(autoSave.lastSaved, 'h:mm:ss a')}`
                      : 'Auto-save off'}
                  </span>
                </div>
              </>
            )}
            {/* Edit mode status — desktop only */}
            {!isMobile && isEditMode && (
              <div className="mt-2 text-xs text-amber-600 font-medium">Editing — save or cancel when done</div>
            )}
          </div>
          {/* Desktop toolbar buttons - hidden on mobile (moved to bottom bar) */}
          {!isMobile && (
          <div className="flex space-x-3 flex-wrap gap-y-2">
            {/* View Mode Toggle */}
            <div className="flex rounded-md border border-steel/30 overflow-hidden">
              <button
                onClick={() => setViewMode('editor')}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  viewMode === 'editor'
                    ? 'bg-navy text-white'
                    : 'bg-white text-navy hover:bg-ice'
                }`}
              >
                Editor
              </button>
              <button
                onClick={() => setViewMode('report')}
                className={`px-3 py-2 text-sm font-medium transition-colors ${
                  viewMode === 'report'
                    ? 'bg-navy text-white'
                    : 'bg-white text-navy hover:bg-ice'
                }`}
              >
                Report View
              </button>
            </div>
            {!isEditMode ? (
              <button
                onClick={handleEdit}
                disabled={!editedAssessment}
                className="px-4 py-2 text-sm font-semibold text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded-md transition-colors disabled:opacity-50"
              >
                Edit
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors disabled:opacity-50"
                  title="Save changes (Ctrl+S / Cmd+S)"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-semibold text-navy bg-white border border-steel/30 hover:bg-ice rounded-md transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
              </>
            )}
            {/* CBTA Export dropdown */}
            <div className="relative inline-block export-dropdown-container">
              <button
                onClick={() => setShowExportDropdown(!showExportDropdown)}
                className="px-4 py-2 text-sm font-semibold text-navy bg-white border border-orca-main hover:bg-navy hover:text-white rounded-md transition-colors disabled:opacity-50"
                disabled={isExportingCBTAPDF || isExportingCBTAWord}
              >
                {isExportingCBTAPDF || isExportingCBTAWord ? 'Exporting...' : 'Export CBTA ▼'}
              </button>
              <div className={`absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-steel/20 z-10 ${showExportDropdown ? 'block' : 'hidden'}`}>
                <button
                  onClick={handleExportCBTAPDF}
                  disabled={isExportingCBTAPDF}
                  className="w-full px-4 py-2 text-sm text-left text-navy hover:bg-ice transition-colors disabled:opacity-50"
                >
                  📄 Export as PDF
                </button>
                <button
                  onClick={handleExportCBTAWord}
                  disabled={isExportingCBTAWord}
                  className="w-full px-4 py-2 text-sm text-left text-navy hover:bg-ice transition-colors disabled:opacity-50"
                >
                  📝 Export as Word
                </button>
              </div>
            </div>
            <button
              onClick={() => setShowVersionHistory(true)}
              disabled={!editedAssessment}
              className="px-4 py-2 text-sm font-semibold text-navy bg-white border border-steel/30 hover:bg-ice rounded-md transition-colors disabled:opacity-40"
            >
              History
            </button>
            {hasInstructorAssessments && (
              <button
                onClick={handleInstructorAssessment}
                className="px-4 py-2 text-sm font-semibold text-navy bg-white border border-steel/30 hover:bg-ice rounded-md transition-colors"
              >
                Instructor Assessment
              </button>
            )}
            <button
              onClick={handlePublish}
              className="px-4 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
            >
              Publish All
            </button>
          </div>
          )}
        </div>
      </div>

      {/* View Mode Content */}
      {viewMode === 'report' ? (
        /* ==================== V2 REPORT VIEW ==================== */
        <div className={`space-y-2 sm:space-y-6 ${isMobile ? 'pb-20' : ''}`}>
          {/* Part A - Session Identification */}
          {v2PartAData && <ORCA360PartA data={v2PartAData} />}

          {/* Part B - TEM Overview */}
          {v2PartBData && <ORCA360PartB data={v2PartBData} />}

          {/* Part C - Competency Assessment */}
          {v2PartCData && <ORCA360PartC data={v2PartCData} />}

          {/* Part D - Root Cause Analysis & Recommendations */}
          {v2PartDData && <ORCA360PartD data={v2PartDData} />}

          {/* Back to Editor Button — desktop only (mobile uses bottom bar) */}
          {!isMobile && (
          <div className="flex justify-center pt-4">
            <button
              onClick={() => setViewMode('editor')}
              className="px-6 py-2 text-sm font-semibold text-navy bg-white border border-orca-main hover:bg-navy hover:text-white rounded-md transition-colors"
            >
              Switch to Editor View for Detailed Editing
            </button>
          </div>
          )}
        </div>
      ) : (
        /* ==================== EDITOR VIEW ==================== */
        <div className={isMobile ? 'pb-20 px-2' : ''}>
          {/* Split-screen toggle button — desktop only */}
          {!isMobile && (
          <div className="mb-4">
            <button
              onClick={() => setShowTranscript(!showTranscript)}
              className="px-4 py-2 text-sm font-semibold text-navy bg-white border border-orca-main hover:bg-navy hover:text-white rounded-md transition-colors"
            >
              {showTranscript ? '← Hide Transcript' : '→ Show Transcript'}
            </button>
            {transcriptIds.length === 0 && (
              <span className="ml-3 text-sm text-steel">
                (No transcripts available for this session)
              </span>
            )}
            {transcriptIds.length > 0 && (
              <span className="ml-3 text-sm text-steel">
                ({transcriptIds.length} transcript{transcriptIds.length > 1 ? 's' : ''} available)
              </span>
            )}
          </div>
          )}

          {/* Mobile: Horizontal scrollable competency pills */}
          {isMobile && (
            <div className="mb-3 -mx-2 px-2">
              <div className="flex overflow-x-auto gap-1.5 pb-2 scrollbar-hide">
                {assessments.map((assessment) => (
                  <button
                    key={assessment.competency}
                    onClick={() => handleCompetencySelect(assessment.competency)}
                    className={`flex-shrink-0 px-2.5 py-1.5 rounded-full text-xs font-semibold transition-colors whitespace-nowrap ${
                      selectedCompetency === assessment.competency
                        ? 'bg-orca-elevate text-white'
                        : 'bg-steel/10 text-navy'
                    }`}
                  >
                    {assessment.competency} {assessment.score}/5
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className={isMobile ? '' : 'flex gap-6 items-stretch'}>
        {/* Left Side: Transcript Viewer — desktop only */}
        {!isMobile && showTranscript && transcriptIds.length > 0 && (
          <div className="w-3/5 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-md overflow-hidden sticky top-4 h-[calc(100vh-8rem)] flex flex-col">
              <TranscriptViewer
                transcriptIds={transcriptIds}
                sessionId={sessionId || undefined}
                sessionInfo={{
                  trainee_name: sessionInfo?.trainee_name,
                  session_date: sessionInfo?.session_date,
                }}
                highlightTimestamp={activeTimestamp || undefined}
              />
            </div>
          </div>
        )}

        {/* Right Side: Competency Sidebar + Assessment Editor */}
        <div className={isMobile ? 'w-full' : `flex gap-6 ${showTranscript && transcriptIds.length > 0 ? 'w-2/5' : 'w-full'}`}>
          {/* Competency Sidebar — desktop only */}
          {!isMobile && (
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-lg shadow-md p-4">
              <h3 className="text-sm font-semibold text-navy mb-4">Competencies</h3>
              <div className="space-y-2">
                {assessments.map((assessment) => (
                  <button
                    key={assessment.competency}
                    onClick={() => handleCompetencySelect(assessment.competency)}
                    className={`w-full text-left px-3 py-2 rounded-md transition-colors ${
                      selectedCompetency === assessment.competency
                        ? 'bg-orca-elevate text-white'
                        : 'hover:bg-ice'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium">
                        {COMPETENCY_NAMES[assessment.competency]}
                      </span>
                      <span className="text-xs font-semibold">
                        {assessment.score}/5
                      </span>
                    </div>
                    <div className="text-xs mt-1 opacity-80">
                      {assessment.competency}
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          )}

          {/* Assessment Editor */}
          {editedAssessment && (
          <div className="flex-1">
            <div className={`bg-white rounded-lg shadow-md ${isMobile ? 'p-3 space-y-3' : 'p-6 space-y-6'}`}>
              {/* Score Section */}
              <div>
                <h3 className={`font-semibold text-navy ${isMobile ? 'text-base mb-2' : 'text-lg mb-3'}`}>
                  {COMPETENCY_NAMES[editedAssessment.competency]} ({editedAssessment.competency})
                </h3>
                <div className="flex items-center space-x-3">
                  <label className="text-sm font-medium text-steel">Score:</label>
                  <div className="flex space-x-1.5">
                    {[1, 2, 3, 4, 5].map((score) => (
                      <button
                        key={score}
                        onClick={() => isEditMode && handleScoreChange(score)}
                        disabled={!isEditMode}
                        className={`${isMobile ? 'w-8 h-8 text-sm' : 'w-10 h-10'} rounded-md font-semibold transition-colors disabled:cursor-default ${
                          editedAssessment.score === score
                            ? 'bg-orca-elevate text-white'
                            : isEditMode
                            ? 'bg-steel/10 text-steel hover:bg-steel/20'
                            : 'bg-steel/10 text-steel'
                        }`}
                      >
                        {score}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Evidence Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-semibold text-navy">Evidence</h4>
                  {isEditMode && (
                    <button
                      onClick={() => setIsAddModalOpen(true)}
                      className="px-3 py-1 text-xs font-semibold text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded-md transition-colors"
                    >
                      + Add Evidence
                    </button>
                  )}
                </div>
                <div className="space-y-2">
                  {editedAssessment.evidence && editedAssessment.evidence.length > 0 ? (
                    editedAssessment.evidence.map((item, idx) => (
                      <div
                        key={idx}
                        className={`${isMobile ? 'p-2' : 'p-3'} rounded-md border-l-4 group hover:shadow-md transition-shadow resize-y overflow-auto min-h-[60px] max-h-[100px] ${
                          item.validation_status === 'VERIFIED' || item.validation_status === 'VERBATIM'
                            ? 'border-green-500 bg-green-50'
                            : item.validation_status === 'PARAPHRASED'
                            ? 'border-yellow-500 bg-yellow-50'
                            : item.validation_status === 'SIMILAR'
                            ? 'border-orange-500 bg-orange-50'
                            : 'border-red-500 bg-red-50'
                        }`}
                        style={{ resize: 'vertical' }}
                      >
                        <div className={isMobile ? '' : 'flex items-start justify-between'}>
                          <div className="flex-1 overflow-hidden">
                            <p className={`text-navy mb-1 line-clamp-2 ${isMobile ? 'text-xs' : 'text-sm'}`}>"{item.quote}"</p>
                            <div className="flex items-center gap-2 flex-wrap">
                              <button
                                onClick={() => handleTimestampClick(item.timestamp)}
                                className="text-xs text-orca-elevate hover:text-orca-elevate/80 hover:underline font-medium transition-colors"
                                title="Jump to transcript location"
                              >
                                @ {item.timestamp}
                              </button>
                              {/* Tier 1-3: Programmatic Validation */}
                              <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded ${getValidationColor(item.validation_status)}`}>
                                {getValidationIcon(item.validation_status)} {item.validation_status}
                              </span>
                              {/* Tier 4: LLM Competency Mapping Validation */}
                              {item.llm_decision && (
                                <span
                                  className={`px-1.5 py-0.5 text-[10px] font-medium rounded border ${
                                    item.llm_decision === 'ACCEPTED'
                                      ? 'text-green-700 bg-green-50 border-green-300'
                                      : item.llm_decision === 'REJECTED'
                                      ? 'text-red-700 bg-red-50 border-red-300'
                                      : 'text-blue-700 bg-blue-50 border-blue-300'
                                  }`}
                                  title={item.llm_reasoning || 'AI validation result'}
                                >
                                  AI: {item.llm_decision === 'ACCEPTED' && '✓'}
                                  {item.llm_decision === 'REJECTED' && '✗'}
                                  {item.llm_decision === 'RECLASSIFIED' && '↻'}
                                  {' '}{item.llm_decision}
                                </span>
                              )}
                              {!isMobile && item.confidence && (
                                <span className="text-xs text-steel">
                                  • Confidence: {(item.confidence * 100).toFixed(0)}%
                                </span>
                              )}
                            </div>
                          </div>
                          {/* Edit/Delete buttons — only in edit mode */}
                          {isEditMode && (
                          <div className={`flex items-center ${isMobile ? 'mt-1.5 gap-2' : 'space-x-2 ml-3'}`}>
                            <div className={`flex space-x-1 ${isMobile ? '' : 'opacity-0 group-hover:opacity-100 transition-opacity'}`}>
                              <button
                                onClick={() => handleEditEvidence(idx)}
                                className="px-2 py-1 text-xs font-semibold text-orca-elevate bg-white border border-orca-elevate hover:bg-orca-elevate hover:text-white rounded transition-colors"
                                title="Edit evidence"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => {
                                  if (confirm('Delete this evidence?')) {
                                    handleDeleteEvidence(idx);
                                  }
                                }}
                                className="px-2 py-1 text-xs font-semibold text-red-600 bg-white border border-red-600 hover:bg-red-600 hover:text-white rounded transition-colors"
                                title="Delete evidence"
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                          )}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-8 bg-steel/5 rounded-md border border-dashed border-steel/30">
                      <p className="text-sm text-steel mb-3">No evidence available</p>
                      {isEditMode && (
                        <button
                          onClick={() => setIsAddModalOpen(true)}
                          className="px-4 py-2 text-sm font-semibold text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded-md transition-colors"
                        >
                          + Add First Evidence
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {/* Tier 4 Bulk LLM Validation */}
                <div className="mt-4">
                  <LLMValidationPanel
                    sessionId={sessionId!}
                    assessmentId={editedAssessment.id}
                    assessmentType="pilot"
                    evidence={editedAssessment.evidence}
                    onComplete={handleBulkLLMValidation}
                  />
                </div>
              </div>

              {/* Analysis Section */}
              <div>
                <h4 className="text-sm font-semibold text-navy mb-2">Root Cause Analysis (5 Whys)</h4>

                {/* RCA Status Indicator */}
                {rcaStatus.isGenerating && rcaStatus.rcaStatus !== 'completed' && (
                  <div className="mb-2 flex items-center gap-2 text-sm">
                    <div className="animate-spin h-4 w-4 border-2 border-orca-elevate/30 border-t-orca-elevate rounded-full" />
                    <span className="text-steel">
                      {rcaStatus.rcaStatus === 'in_progress'
                        ? 'Generating Root Cause Analysis...'
                        : 'Root Cause Analysis queued for generation...'}
                    </span>
                  </div>
                )}

                {rcaStatus.rcaStatus === 'completed' && rcaStatus.rcaCompletedAt && (
                  <div className="mb-2 text-sm text-green-600 flex items-center gap-1">
                    <span>✓</span>
                    <span>RCA completed at {new Date(rcaStatus.rcaCompletedAt).toLocaleTimeString()}</span>
                  </div>
                )}

                {rcaStatus.rcaStatus === 'failed' && (
                  <div className="mb-2 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span>
                    <span>RCA generation failed - please regenerate</span>
                  </div>
                )}

                <textarea
                  value={editedAssessment.analysis || ''}
                  onChange={(e) => handleAnalysisChange(e.target.value)}
                  readOnly={!isEditMode}
                  className={`w-full px-3 py-2 bg-white border border-steel/30 rounded-md text-sm ${isMobile ? 'h-24' : 'h-32'} ${isEditMode ? 'focus:outline-none focus:ring-2 focus:ring-orca-elevate' : 'cursor-default bg-steel/5'}`}
                  placeholder="Why-Why-Why-Why-Why-Why (5 Whys)-Why analysis..."
                />
              </div>

              {/* Goals Section */}
              <div>
                <h4 className="text-sm font-semibold text-navy mb-2">Improvement Goals (SMART)</h4>

                {/* SMART Goals Status Indicator */}
                {rcaStatus.isGenerating && rcaStatus.smartStatus !== 'completed' && (
                  <div className="mb-2 flex items-center gap-2 text-sm">
                    <div className="animate-spin h-4 w-4 border-2 border-orca-elevate/30 border-t-orca-elevate rounded-full" />
                    <span className="text-steel">
                      {rcaStatus.smartStatus === 'in_progress'
                        ? 'Generating SMART Goals...'
                        : 'SMART Goals queued for generation...'}
                    </span>
                  </div>
                )}

                {rcaStatus.smartStatus === 'completed' && rcaStatus.smartCompletedAt && (
                  <div className="mb-2 text-sm text-green-600 flex items-center gap-1">
                    <span>✓</span>
                    <span>SMART Goals completed at {new Date(rcaStatus.smartCompletedAt).toLocaleTimeString()}</span>
                  </div>
                )}

                {rcaStatus.smartStatus === 'failed' && (
                  <div className="mb-2 text-sm text-red-600 flex items-center gap-1">
                    <span>⚠</span>
                    <span>SMART Goals generation failed - please regenerate</span>
                  </div>
                )}

                <textarea
                  value={editedAssessment.goals || ''}
                  onChange={(e) => handleGoalsChange(e.target.value)}
                  readOnly={!isEditMode}
                  className={`w-full px-3 py-2 bg-white border border-steel/30 rounded-md text-sm ${isMobile ? 'h-24' : 'h-32'} ${isEditMode ? 'focus:outline-none focus:ring-2 focus:ring-orca-elevate' : 'cursor-default bg-steel/5'}`}
                  placeholder="SMART improvement goals..."
                />
              </div>

              {/* Status */}
              <div className={`border-t border-steel/20 ${isMobile ? 'pt-2' : 'pt-4'}`}>
                <div className={`${isMobile ? 'text-[10px]' : 'text-xs'} text-steel ${isMobile ? '' : 'flex items-center justify-between'}`}>
                  <div>
                    Status: <span className="font-medium capitalize">{editedAssessment.status.replace('_', ' ')}</span>
                    {' • '}
                    Version: {editedAssessment.version}
                  </div>
                  <div>
                    Updated: {new Date(editedAssessment.updated_at).toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
          </div>
        </div>
        </div>
      )}

      {/* Evidence Edit Modal */}
      {editedAssessment && selectedEvidenceIndex !== null && editedAssessment.evidence[selectedEvidenceIndex] && (
        <EvidenceEditModal
          evidence={editedAssessment.evidence[selectedEvidenceIndex]}
          isOpen={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedEvidenceIndex(null);
          }}
          onSave={handleSaveEvidence}
          onDelete={() => {
            handleDeleteEvidence(selectedEvidenceIndex);
            setIsEditModalOpen(false);
            setSelectedEvidenceIndex(null);
          }}
          sessionId={sessionId}
          assessmentId={editedAssessment.id}
          assessmentType="pilot"
        />
      )}

      {/* Add Evidence Modal */}
      <AddEvidenceModal
        sessionId={sessionId || ''}
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onAdd={handleAddEvidence}
      />
        </div>
      </div>

      {/* Version History Drawer */}
      <VersionHistoryDrawer
        assessmentId={editedAssessment?.id}
        assessmentType="pilot"
        isOpen={showVersionHistory}
        onClose={() => setShowVersionHistory(false)}
        onRestore={handleVersionRestore}
      />

      {/* ==================== MOBILE BOTTOM BAR ==================== */}
      {isMobile && (
        <div
          className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 z-50 pb-safe"
          style={{ boxShadow: '0 -2px 10px rgba(0,0,0,0.06)' }}
        >
          <div className="flex items-center justify-between px-3 py-1.5 max-w-lg mx-auto">
            {/* Editor / Report toggle */}
            <div className="flex rounded-lg border border-steel/30 overflow-hidden">
              <button
                onClick={() => setViewMode('editor')}
                className={`px-2.5 py-1 text-[10px] font-semibold transition-colors ${
                  viewMode === 'editor'
                    ? 'bg-navy text-white'
                    : 'bg-white text-slate-600'
                }`}
              >
                Editor
              </button>
              <button
                onClick={() => setViewMode('report')}
                className={`px-2.5 py-1 text-[10px] font-semibold transition-colors ${
                  viewMode === 'report'
                    ? 'bg-navy text-white'
                    : 'bg-white text-slate-600'
                }`}
              >
                Report
              </button>
            </div>

            {/* Autosave micro-toggle */}
            <button
              type="button"
              role="switch"
              aria-checked={autosaveEnabled}
              disabled
              className="flex items-center gap-1"
              title="Auto-save is disabled"
            >
              <span className="text-[9px] text-slate-400">Auto</span>
              <div className={`relative inline-flex h-3.5 w-6 items-center rounded-full transition-colors ${
                autosaveEnabled ? 'bg-green-500' : 'bg-gray-300'
              }`}>
                <span className={`inline-block h-2.5 w-2.5 transform rounded-full bg-white shadow transition-transform ${
                  autosaveEnabled ? 'translate-x-2.5' : 'translate-x-0.5'
                }`} />
              </div>
            </button>

            {/* Edit / Save / Cancel */}
            {!isEditMode ? (
              <button
                onClick={handleEdit}
                disabled={!editedAssessment}
                className="px-3 py-1 text-[10px] font-semibold rounded-lg bg-orca-elevate text-white disabled:opacity-50"
              >
                Edit
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={isSaving}
                  className="px-3 py-1 text-[10px] font-semibold rounded-lg bg-green-600 text-white disabled:opacity-50"
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={isSaving}
                  className="px-3 py-1 text-[10px] font-semibold rounded-lg bg-slate-100 text-slate-600 disabled:opacity-50"
                >
                  Cancel
                </button>
              </>
            )}

            {/* More menu (⋮) */}
            <div className="relative">
              <button
                onClick={() => setShowMobileMenu(!showMobileMenu)}
                className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-slate-100 text-slate-600"
              >
                <MoreVertical size={14} />
              </button>

              {showMobileMenu && (
                <>
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowMobileMenu(false)}
                  />
                  <div className="absolute bottom-9 right-0 w-40 bg-white rounded-lg shadow-lg border border-steel/20 z-50 py-1">
                    <button
                      onClick={() => { setShowMobileMenu(false); handleExportCBTAPDF(); }}
                      disabled={isExportingCBTAPDF}
                      className="w-full px-3 py-2 text-xs text-left text-navy hover:bg-ice transition-colors disabled:opacity-50"
                    >
                      Export as PDF
                    </button>
                    <button
                      onClick={() => { setShowMobileMenu(false); handleExportCBTAWord(); }}
                      disabled={isExportingCBTAWord}
                      className="w-full px-3 py-2 text-xs text-left text-navy hover:bg-ice transition-colors disabled:opacity-50"
                    >
                      Export as Word
                    </button>
                    {hasInstructorAssessments && (
                      <>
                        <div className="border-t border-steel/10 my-0.5" />
                        <button
                          onClick={() => { setShowMobileMenu(false); navigate(`/sessions/${sessionId}/edit-instructor-assessment`); }}
                          className="w-full px-3 py-2 text-xs text-left text-navy hover:bg-ice transition-colors"
                        >
                          View Instructor Assessment
                        </button>
                      </>
                    )}
                    <div className="border-t border-steel/10 my-0.5" />
                    <button
                      onClick={() => { setShowMobileMenu(false); setShowVersionHistory(true); }}
                      disabled={!editedAssessment}
                      className="w-full px-3 py-2 text-xs text-left text-navy hover:bg-ice transition-colors disabled:opacity-50"
                    >
                      Version History
                    </button>
                    <div className="border-t border-steel/10 my-0.5" />
                    <button
                      onClick={() => { setShowMobileMenu(false); handlePublish(); }}
                      className="w-full px-3 py-2 text-xs text-left text-green-700 font-medium hover:bg-green-50 transition-colors"
                    >
                      Publish All
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
